import axios from 'axios';
import { CustomEventWithDetail, html, View } from 'rune-ts';
import { generateSearchUrl } from '../../../../features/Search/const/url';
import { SearchApiS } from '../../../../features/Search/outbound/share';
import { SearchModuleData } from '../../../../features/Search/type';
import { makeSearchScreenNavigate } from '../../../../shared/app/navigate';
import { isApp, mpsPostMessage } from '../../../../shared/app/util';
import { typo } from '../../../../shared/typography/typo';
import { htmlIf } from '../../../../shared/util';
import { ButtonKeyword } from '../../atoms/ButtonKeyword/ButtonKeyword';
import { CloseIcon } from '../../atoms/Icon';
import { ProductCardListView, ProductCardListPlugin } from '../ProductCardList/ProductCardListView.ts';
import { SearchModuleController } from './controllers/SearchModuleController';
import klass from './SearchModule.module.scss';
import { AutoCompleteListOption } from './view/AutoCompleteList';
import {
  AutoCompleteListHideEvent,
  AutoCompleteListShowEvent,
  AutoCompleteSearchInput,
  EmptySubmitEvent,
  NotEmptySubmitEvent,
} from './view/AutoCompleteSearchInput';
import { RecentSearchesSection } from './view/RecentSearchesSection';
import util_klass from './view/SearchModuleUtil.module.scss';
import { ProductCardSize } from '@services/MarppleShop/features/ProductList/type';
import { postSearchLogApi } from '@services/MarppleShop/features/Search/api';
import { DetachedCardCurationView } from '@services/MarppleShop/renderApp/components/cells/SearchModule/DetachedCardCurationView.ts';

export interface SearchData {
  name: string;
}

type SearchModuleOption = Pick<AutoCompleteListOption, 'highlight'> & {
  klass?: string;
  without_visible_interaction?: boolean;
  is_mobile: boolean;
};

export class SearchModuleCloseEvent extends CustomEventWithDetail<string> {}

export class SearchModule extends View<SearchModuleData> {
  AutoCompleteSearchInputView: AutoCompleteSearchInput;
  RecentSearchesSectionView: RecentSearchesSection;
  SearchModuleController: SearchModuleController;

  constructor(
    data: SearchModuleData,
    private option: SearchModuleOption,
  ) {
    super(data, option);
    this.AutoCompleteSearchInputView = new AutoCompleteSearchInput({
      value: this.data.value,
      placeholder: this.data.placeholder,
      container_klass: klass.search_input,
      debounce_ms: 200,
      transparent: !option.is_mobile,
      off_reset: !option.is_mobile,
      placeholder_gray50: true,
      highlight: option.highlight,
    });
    this.RecentSearchesSectionView = new RecentSearchesSection({
      recent_searches: [],
      is_mobile: option.is_mobile,
    });
    this.SearchModuleController = new SearchModuleController(this);
  }

  override template() {
    const _klass = this.option.without_visible_interaction
      ? util_klass.visible
      : `${util_klass.hidden} ${util_klass.hiding}`;
    const is_mobile = this.option.is_mobile;
    const theme = is_mobile ? 'light' : 'dark';
    const { popular_searches, recommended_products = [], curation } = this.data;

    return html`<div class="${klass.search_module} ${_klass} ${this.option.klass}">
      <div class="${klass.input_section}">
        ${this.AutoCompleteSearchInputView}
        ${htmlIf(html`<span class="${klass.close_icon}">${CloseIcon()}</span>`, is_mobile)}
      </div>

      <!-- 최근 검색어 -->
      ${this.RecentSearchesSectionView}

      <!-- 인기 검색어 -->
      <div class="${klass.popular_section}">
        <span class="${klass.title} ${typo('12_medium')}">${ET('mps2::search::popular_search')}</span>
        <div class="${klass.searches_container}">
          ${popular_searches.map(({ name }) => {
            return html`<div data-search="${name}" class="${klass.popular_search}">
              ${new ButtonKeyword({ name, theme })}
            </div>`;
          })}
        </div>
      </div>

      <!-- 가장 최근 큐레이션 -->
      <div class="${klass.curation_section}">
        <span class="${klass.title} ${typo('12_medium')}">${ET('mps2::search::curation')}</span>
        ${curation
          ? new DetachedCardCurationView({
              device: 'Mobile',
              layout: 'Thumbnail',
              ...curation,
            })
          : ''}
      </div>

      <!-- 추천 상품 -->
      ${recommended_products.length
        ? html`<div class="${klass.recommend_section}">
            <span class="${klass.title} ${typo('12_medium')}">${ET('mps2::search::recommend')}</span>
            ${new ProductCardListView(
              { products: recommended_products },
              {
                size: ProductCardSize.xSmall,
                resize_width: 500,
                container_klass: klass.products_container,
                card_klass: klass.product_card,
                hide_badges: is_mobile,
                hide_tag: is_mobile,
                grid: {
                  enabled: false,
                },
                plugins: {
                  [ProductCardListPlugin.swiper]: {
                    enabled: true,
                    gap: 16,
                    width: 128,
                    swiperOptions: {
                      autoplay: false,
                      speed: 600,
                    },
                  },
                },
              },
            )}
          </div>`
        : ''}
    </div>`;
  }

  override async onRender() {
    // FIXME: api로 래핑
    const [{ data: recents }, { auto_completes }] = await Promise.all([
      axios.get<{ queries: string[] }>(`/${ET.lang}/@api/keywords/history`),
      SearchApiS.getAutoCompleteListApi({ search: this.data.value }),
    ]);

    this.AutoCompleteSearchInputView.setData({ auto_completes, value: this.data.value ?? '' });
    this.RecentSearchesSectionView.setData({
      recent_searches: recents.queries.map((search) => ({ name: search })),
    });
    this.redraw();

    if (auto_completes.length) {
      this.RecentSearchesSectionView.hide();
    } else {
      this.RecentSearchesSectionView.show();
    }
  }

  override onMount() {
    this.delegate(AutoCompleteListHideEvent, AutoCompleteSearchInput, () => {
      this.RecentSearchesSectionView.show();
    });

    this.delegate(AutoCompleteListShowEvent, AutoCompleteSearchInput, () => {
      this.RecentSearchesSectionView.hide();
    });

    this.delegate(EmptySubmitEvent, AutoCompleteSearchInput, () => {
      this.SearchModuleController.shake();
    });

    this.delegate('click', `.${klass.popular_search}`, (e) => {
      const el = e.currentTarget as HTMLElement;
      const search = el.dataset.search;
      if (search) {
        postSearchLogApi({ search, is_popular_search_word: true });
        return this.confirmSearch(search);
      }
    });

    this.delegate(NotEmptySubmitEvent, AutoCompleteSearchInput, (e) => {
      const search = e.detail;
      if (search) {
        postSearchLogApi({ search, is_popular_search_word: false });
        return this.confirmSearch(search);
      }
    });

    this.SearchModuleController.onMount();

    // mobile only
    if (this.option.is_mobile) {
      this.delegate('click', `.${klass.close_icon}`, () => {
        this.dispatchEvent(SearchModuleCloseEvent, {
          bubbles: true,
          detail: this.AutoCompleteSearchInputView.getInputValue(),
        });
      });
    }
  }

  open() {
    this.SearchModuleController.open();
    this.AutoCompleteSearchInputView.focus();
  }

  close(): string {
    this.SearchModuleController.close();
    return this.AutoCompleteSearchInputView.getInputValue();
  }

  // 검색어 최근 검색어에 추가하고, 결과로 이동
  private async confirmSearch(search: string) {
    const url = generateSearchUrl(search);
    this.AutoCompleteSearchInputView.setInputValue(search);
    await this.SearchModuleController.saveRecentSearch(search);

    if (isApp()) {
      mpsPostMessage(makeSearchScreenNavigate(url));
    } else {
      location.href = url;
    }
  }
}
